/**
 * Bundle 98 - Legacy Survey Recipients incident admin page.
 *
 * Super_admin only. Enumerates every real attendee who received a
 * survey via the decommissioned `/api/admin/events/../survey/send`
 * legacy path and lets the admin preview + send the apology email
 * with a fresh Bundle 76+ System 2 invitation.
 *
 * Backend endpoints:
 *   GET  /api/admin/incident/legacy-survey-recipients
 *   GET  /api/admin/incident/legacy-survey-recipients.csv
 *   POST /api/admin/incident/legacy-survey-recipients/send-apology
 *
 * Guardrails: no em/en dashes, no eslint-disable, no photo/image
 * in alt attributes.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertTriangle, Download, Mail, RefreshCw, Send } from 'lucide-react';
import api from '@/utils/api';

const _maskEmail = (email) => {
  if (!email || typeof email !== 'string') return '(no email)';
  const [local, domain] = email.split('@');
  if (!domain) return email;
  const head = (local || '').slice(0, 1);
  return `${head}***@${domain}`;
};

const _statusPill = (recipient) => {
  if (recipient.resent_at) {
    return { label: 'Apology sent', tone: 'green' };
  }
  if (recipient.invalidated_at) {
    return { label: 'Invalidated', tone: 'grey' };
  }
  if (recipient.has_started_response) {
    return { label: 'Started', tone: 'amber' };
  }
  return { label: 'Not sent', tone: 'red' };
};

const Pill = ({ tone, children }) => {
  const map = {
    green: 'bg-green-50 text-green-800 ring-1 ring-green-200',
    red: 'bg-red-50 text-red-800 ring-1 ring-red-200',
    amber: 'bg-amber-50 text-amber-900 ring-1 ring-amber-200',
    grey: 'bg-neutral-100 text-neutral-700 ring-1 ring-neutral-200',
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${map[tone] || map.grey}`}>
      {children}
    </span>
  );
};


const IncidentLegacySurveyRecipientsPage = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [unmask, setUnmask] = useState(false);
  const [previewRow, setPreviewRow] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState({ done: 0, total: 0 });
  const [flash, setFlash] = useState(null);
  const [signerFirstName, setSignerFirstName] = useState('The Dostination Team');
  const [signerTitle, setSignerTitle] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.get('/admin/incident/legacy-survey-recipients');
      setRows(data.recipients || []);
      setSummary(data.summary || null);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load recipients.');
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filteredRows = rows;
  const allChecked = filteredRows.length > 0 && filteredRows.every(
    (r) => selected.has(r.token_id),
  );
  const toggleAll = () => {
    setSelected((prev) => {
      if (allChecked) return new Set();
      const next = new Set(prev);
      filteredRows.forEach((r) => next.add(r.token_id));
      return next;
    });
  };
  const toggleOne = (tokenId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(tokenId)) next.delete(tokenId);
      else next.add(tokenId);
      return next;
    });
  };

  const previewApology = async (row) => {
    // We call send-apology with dry_run=true to render the exact
    // templated body so preview and send share one code path.
    try {
      const { data } = await api.post(
        '/admin/incident/legacy-survey-recipients/send-apology',
        {
          token_ids: [row.token_id],
          signer_first_name: signerFirstName,
          signer_title: signerTitle,
          dry_run: true,
        },
      );
      const preview = (data.results || []).find(
        (r) => r.token_id === row.token_id,
      );
      setPreviewRow({ row, preview });
    } catch (e) {
      setFlash({
        ok: false,
        msg: e?.response?.data?.detail || 'Preview failed.',
      });
    }
  };

  const sendSelected = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(
      `Send apology to ${ids.length} recipient(s)? The server rate-limits to 30 per minute; the FE also paces.`,
    )) return;
    setSending(true);
    setSendProgress({ done: 0, total: ids.length });
    // Chunk into 25-per-request batches so a single response ties
    // back to a manageable audit trail.
    const CHUNK = 25;
    let done = 0;
    for (let i = 0; i < ids.length; i += CHUNK) {
      const batch = ids.slice(i, i + CHUNK);
      try {
        await api.post(
          '/admin/incident/legacy-survey-recipients/send-apology',
          {
            token_ids: batch,
            signer_first_name: signerFirstName,
            signer_title: signerTitle,
            dry_run: false,
          },
        );
        done += batch.length;
        setSendProgress({ done, total: ids.length });
      } catch (e) {
        setFlash({
          ok: false,
          msg: e?.response?.data?.detail
            || `Batch failed at index ${i}.`,
        });
        break;
      }
      // 2 s pacing per 25-batch so we stay comfortably under 30/min.
      if (i + CHUNK < ids.length) {
        await new Promise((res) => setTimeout(res, 2000));
      }
    }
    setSending(false);
    setFlash({
      ok: true,
      msg: `Sent apology to ${done} of ${ids.length} recipient(s).`,
    });
    setSelected(new Set());
    await load();
  };

  const downloadCsv = async () => {
    const r = await api.get(
      '/admin/incident/legacy-survey-recipients.csv',
      { responseType: 'blob' },
    );
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `legacy-survey-recipients-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="p-8 space-y-6 max-w-7xl" data-testid="incident-legacy-survey-recipients-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-primary-900">
            Incident: Legacy survey recipients
          </h1>
          <p className="text-neutral-600 text-sm mt-1 max-w-3xl">
            Real attendees who received a survey email via the
            decommissioned legacy path. Preview and send an apology
            with a fresh survey link that uses the current Bundle 89
            schema.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load}
            data-testid="incident-reload"
            disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Reload
          </Button>
          <Button variant="outline" size="sm"
            onClick={downloadCsv} data-testid="incident-csv"
            disabled={rows.length === 0}>
            <Download className="h-3.5 w-3.5 mr-1" />
            CSV
          </Button>
        </div>
      </div>

      {summary ? (
        <Card data-testid="incident-summary">
          <CardContent className="p-4 grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
            <div><div className="text-neutral-500 text-xs">Total recipients</div><div className="font-semibold text-primary-900">{summary.total_recipients}</div></div>
            <div><div className="text-neutral-500 text-xs">Distinct events</div><div className="font-semibold text-primary-900">{summary.distinct_events}</div></div>
            <div><div className="text-neutral-500 text-xs">First send</div><div className="font-semibold text-primary-900">{summary.first_send ? new Date(summary.first_send).toLocaleString() : '-'}</div></div>
            <div><div className="text-neutral-500 text-xs">Last send</div><div className="font-semibold text-primary-900">{summary.last_send ? new Date(summary.last_send).toLocaleString() : '-'}</div></div>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <Label className="text-xs text-neutral-600 inline-flex items-center gap-2">
              <Checkbox checked={unmask} onCheckedChange={setUnmask}
                data-testid="incident-unmask" />
              Unmask emails
            </Label>
            <div className="ml-auto flex items-center gap-2">
              <Input value={signerFirstName}
                onChange={(e) => setSignerFirstName(e.target.value)}
                placeholder="Signer first name"
                className="w-56" data-testid="incident-signer-first" />
              <Input value={signerTitle}
                onChange={(e) => setSignerTitle(e.target.value)}
                placeholder="Signer title (optional)"
                className="w-56" data-testid="incident-signer-title" />
              <Button onClick={sendSelected}
                disabled={sending || selected.size === 0}
                className="bg-primary-800 text-white hover:bg-primary-900"
                data-testid="incident-send-apology">
                <Send className="h-3.5 w-3.5 mr-1" />
                Send apology to selected ({selected.size})
              </Button>
            </div>
          </div>

          {sending ? (
            <div className="text-xs text-primary-800"
              data-testid="incident-send-progress">
              Sending {sendProgress.done} / {sendProgress.total} ...
            </div>
          ) : null}
          {flash ? (
            <div className={`rounded-md border px-3 py-2 text-xs ${flash.ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}
              data-testid="incident-flash">{flash.msg}</div>
          ) : null}
          {err ? (
            <div className="text-xs text-red-700"
              data-testid="incident-error">
              <AlertTriangle className="inline h-3.5 w-3.5 mr-1" />
              {err}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="p-6 text-neutral-500 text-sm">Loading recipients...</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-neutral-600" data-testid="incident-empty">
              <Mail className="h-6 w-6 mx-auto mb-2 text-neutral-400" aria-hidden="true" />
              No affected recipients. All clear.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-neutral-700 text-xs uppercase tracking-wide">
                <tr>
                  <th className="p-2 text-left">
                    <Checkbox checked={allChecked}
                      onCheckedChange={toggleAll}
                      data-testid="incident-check-all" />
                  </th>
                  <th className="p-2 text-left">Name</th>
                  <th className="p-2 text-left">Email</th>
                  <th className="p-2 text-left">Event</th>
                  <th className="p-2 text-left">Sent at</th>
                  <th className="p-2 text-left">Started?</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => {
                  const p = _statusPill(r);
                  return (
                    <tr key={r.token_id}
                      className="border-t border-neutral-100 hover:bg-neutral-50"
                      data-testid={`incident-row-${r.token_id.slice(0, 8)}`}>
                      <td className="p-2">
                        <Checkbox checked={selected.has(r.token_id)}
                          onCheckedChange={() => toggleOne(r.token_id)}
                          data-testid={`incident-check-${r.token_id.slice(0, 8)}`} />
                      </td>
                      <td className="p-2 text-primary-900 font-medium">{r.full_name || '(unknown)'}</td>
                      <td className="p-2 text-neutral-700 font-mono text-xs">
                        {unmask ? (r.email || '-') : _maskEmail(r.email)}
                      </td>
                      <td className="p-2 text-neutral-700">{r.event_name || r.event_id?.slice(0, 8)}</td>
                      <td className="p-2 text-neutral-600 text-xs">{r.sent_at ? new Date(r.sent_at).toLocaleString() : '-'}</td>
                      <td className="p-2 text-neutral-700 text-xs">{r.has_started_response ? 'Yes' : 'No'}</td>
                      <td className="p-2"><Pill tone={p.tone}>{p.label}</Pill></td>
                      <td className="p-2">
                        <Button size="sm" variant="outline" onClick={() => previewApology(r)}
                          data-testid={`incident-preview-${r.token_id.slice(0, 8)}`}>
                          Preview
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!previewRow} onOpenChange={(o) => !o && setPreviewRow(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Apology preview</DialogTitle>
          </DialogHeader>
          {previewRow ? (
            <div className="space-y-3 text-sm">
              <div className="text-xs text-neutral-600">To: <span className="font-mono">{previewRow.preview?.recipient || previewRow.row.email}</span></div>
              <div className="text-xs text-neutral-600">Fresh survey link: <a className="text-primary-800 underline" href={previewRow.preview?.new_link}>{previewRow.preview?.new_link}</a></div>
              <div className="rounded-md border border-neutral-200 bg-white p-4"
                dangerouslySetInnerHTML={{ __html: previewRow.preview?.html_preview || '(No preview)' }} />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IncidentLegacySurveyRecipientsPage;
